import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { apiErrorMessage } from '@/shared/api-client';
import { useCreateUser } from './api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

const MIN_PASSWORD_LENGTH = 12;

export function CreateUserDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation('common');
  const createUser = useCreateUser();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setEmail('');
    setDisplayName('');
    setPassword('');
    setError(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('users.errors.passwordTooShort'));
      return;
    }
    try {
      await createUser.mutateAsync({
        email: email.trim(),
        displayName: displayName.trim() || undefined,
        password,
      });
      handleOpenChange(false);
      onCreated();
    } catch (caught) {
      setError(apiErrorMessage(t, 'users', caught));
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('users.create.title')}</DialogTitle>
            <DialogDescription>{t('users.create.description')}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="create-user-email">{t('users.fields.email')}</Label>
            <Input
              id="create-user-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="create-user-name">{t('users.fields.displayName')}</Label>
            <Input
              id="create-user-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="create-user-password">{t('users.fields.password')}</Label>
            <Input
              id="create-user-password"
              type="password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending ? t('users.create.submitting') : t('users.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
